/**
 * Module-level shared tutorial state for BarbershopTalk.
 * Continuity: active journey, arrival tips, Continue exploring launcher.
 */

import { reactive } from 'vue';
import store from '@/store/index';
import LoggerService from '@/services/LoggerService';
import router from '@/router';
import ArticleService from '@/services/ArticleService';
import {
  TUTORIAL_VERSION,
  STORAGE_KEYS,
  INTEREST_ORDER,
  INTERESTS,
  PRIMARY_ITEMS,
  OPTIONAL_ITEMS,
  COACHMARKS,
  JOURNEY_MAX_AGE_MS,
  PAGE_HIGHLIGHTS_VERSION,
  getAllItemIds,
  getAllCoachmarkIds,
  getPrimaryItem,
  getOptionalItem,
  getCoachmark,
  pathMatchesExpected,
  getPageTour,
  resolvePageTourIdForPath,
} from '@/constants/tutorialConfig';

function readJsonArray(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeJsonArray(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

function logTutorialEvent(event, articleId = 0) {
  try {
    const userId = store.state.user?.id ?? store.state.user?.userId ?? null;
    const sessionId = store.state.sessionId || localStorage.getItem('sessionId');
    LoggerService.logger({
      user_id: userId,
      articleId: articleId || 0,
      event,
      sessionId,
      timeDwell: 0,
    }).catch(() => {});
  } catch {
    /* analytics must never break UX */
  }
}

const state = reactive({
  hydrated: false,
  homeReady: false,
  welcomeCompleted: false,
  tutorialSkipped: false,
  tutorialStarted: false,
  interest: INTERESTS.all,
  completedItems: [],
  dismissedCoachmarks: [],
  welcomeOpen: false,
  welcomeScreen: 1,
  checklistOpen: false,
  activeCoachmarkId: null,
  pendingCoachmarkId: null,
  activeInterrupt: null, // 'welcome' | 'coachmark' | null
  activeTutorialItemId: null,
  pendingArrivalId: null,
  activeJourneyStartedAt: null,
  arrivalShownForItem: null,
  activeJourneyPath: null,
  launcherDismissedSession: false,
  journeyLoading: false,
  // Page Highlights (separate from arrival / feature coachmarks)
  activePageTourId: null,
  activePageTourStep: 0,
  activePageTourSteps: [],
  pageTourInvitationVisible: false,
  pageTourInvitationId: null,
  completedPageTours: [],
  dismissedPageTourInvitations: [],
  pageTourTabRequest: null,
});

const PAGE_TOUR_HIGHLIGHT_CLASS = 'bst-page-tour-target';

let journeyLoadingTimer = null;
const JOURNEY_LOADING_MAX_MS = 10000;

function beginJourneyLoading() {
  state.journeyLoading = true;
  if (journeyLoadingTimer) {
    clearTimeout(journeyLoadingTimer);
    journeyLoadingTimer = null;
  }
  journeyLoadingTimer = setTimeout(() => {
    endJourneyLoading();
  }, JOURNEY_LOADING_MAX_MS);
}

function endJourneyLoading() {
  if (journeyLoadingTimer) {
    clearTimeout(journeyLoadingTimer);
    journeyLoadingTimer = null;
  }
  state.journeyLoading = false;
}
function persistJourney() {
  try {
    if (state.activeTutorialItemId) {
      localStorage.setItem(STORAGE_KEYS.activeItem, state.activeTutorialItemId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.activeItem);
    }
    if (state.pendingArrivalId) {
      localStorage.setItem(STORAGE_KEYS.pendingArrival, state.pendingArrivalId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.pendingArrival);
    }
    if (state.activeJourneyStartedAt) {
      localStorage.setItem(STORAGE_KEYS.journeyStartedAt, String(state.activeJourneyStartedAt));
    } else {
      localStorage.removeItem(STORAGE_KEYS.journeyStartedAt);
    }
    if (state.activeJourneyPath) {
      localStorage.setItem(STORAGE_KEYS.journeyPath, state.activeJourneyPath);
    } else {
      localStorage.removeItem(STORAGE_KEYS.journeyPath);
    }
    localStorage.setItem(
      STORAGE_KEYS.tutorialStarted,
      state.tutorialStarted ? 'true' : 'false'
    );
  } catch {
    /* ignore */
  }
}

function clearActiveJourney({ cancelled = false } = {}) {
  const old = state.activeTutorialItemId;
  if (cancelled && old) {
    logTutorialEvent(`tutorial_journey_cancelled_${old}`);
  }
  if (
    state.activeCoachmarkId &&
    state.activeCoachmarkId.startsWith('arrival-')
  ) {
    state.activeCoachmarkId = null;
    if (state.activeInterrupt === 'coachmark') releaseInterrupt('coachmark');
  }
  if (state.pendingCoachmarkId && String(state.pendingCoachmarkId).startsWith('arrival-')) {
    state.pendingCoachmarkId = null;
  }
  state.activeTutorialItemId = null;
  state.pendingArrivalId = null;
  state.activeJourneyStartedAt = null;
  state.arrivalShownForItem = null;
  state.activeJourneyPath = null;
  persistJourney();
}

function expireStaleJourney() {
  if (!state.activeTutorialItemId || !state.activeJourneyStartedAt) return;
  const age = Date.now() - Number(state.activeJourneyStartedAt);
  if (age > JOURNEY_MAX_AGE_MS) {
    clearActiveJourney({ cancelled: true });
  }
}

function hydrateFromStorage() {
  if (state.hydrated) return;

  const storedVersion = localStorage.getItem(STORAGE_KEYS.version);
  const versionChanged =
    storedVersion == null || Number(storedVersion) !== TUTORIAL_VERSION;

  let welcomeCompleted = localStorage.getItem(STORAGE_KEYS.welcomeCompleted) === 'true';
  let tutorialSkipped = localStorage.getItem(STORAGE_KEYS.skipped) === 'true';
  let tutorialStarted = localStorage.getItem(STORAGE_KEYS.tutorialStarted) === 'true';
  let interest = localStorage.getItem(STORAGE_KEYS.interest) || INTERESTS.all;
  if (!Object.values(INTERESTS).includes(interest)) interest = INTERESTS.all;

  let completedItems = readJsonArray(STORAGE_KEYS.completed);
  let dismissedCoachmarks = readJsonArray(STORAGE_KEYS.dismissed);

  const validItemIds = new Set(getAllItemIds());
  const validCoachmarkIds = new Set(getAllCoachmarkIds());
  completedItems = completedItems.filter((id) => validItemIds.has(id));
  dismissedCoachmarks = dismissedCoachmarks.filter((id) => validCoachmarkIds.has(id));

  let activeTutorialItemId = localStorage.getItem(STORAGE_KEYS.activeItem);
  let pendingArrivalId = localStorage.getItem(STORAGE_KEYS.pendingArrival);
  let activeJourneyStartedAt = localStorage.getItem(STORAGE_KEYS.journeyStartedAt);
  let activeJourneyPath = localStorage.getItem(STORAGE_KEYS.journeyPath);

  if (activeTutorialItemId && !validItemIds.has(activeTutorialItemId)) {
    activeTutorialItemId = null;
    pendingArrivalId = null;
    activeJourneyStartedAt = null;
    activeJourneyPath = null;
  }
  if (pendingArrivalId && !validCoachmarkIds.has(pendingArrivalId)) {
    pendingArrivalId = null;
  }
  if (activeJourneyPath && !String(activeJourneyPath).startsWith('/')) {
    activeJourneyPath = null;
  }

  if (versionChanged) {
    welcomeCompleted = false;
    tutorialSkipped = false;
    try {
      localStorage.setItem(STORAGE_KEYS.version, String(TUTORIAL_VERSION));
      localStorage.removeItem(STORAGE_KEYS.welcomeCompleted);
      localStorage.removeItem(STORAGE_KEYS.skipped);
      writeJsonArray(STORAGE_KEYS.completed, completedItems);
      writeJsonArray(STORAGE_KEYS.dismissed, dismissedCoachmarks);
    } catch {
      /* ignore */
    }
  }

  try {
    state.launcherDismissedSession =
      sessionStorage.getItem(STORAGE_KEYS.launcherDismissedSession) === 'true';
  } catch {
    state.launcherDismissedSession = false;
  }

  state.welcomeCompleted = welcomeCompleted;
  state.tutorialSkipped = tutorialSkipped;
  state.tutorialStarted = tutorialStarted || welcomeCompleted;
  state.interest = interest;
  state.completedItems = completedItems;
  state.dismissedCoachmarks = dismissedCoachmarks;
  state.activeTutorialItemId = activeTutorialItemId;
  state.pendingArrivalId = pendingArrivalId;
  state.activeJourneyStartedAt = activeJourneyStartedAt
    ? Number(activeJourneyStartedAt)
    : null;
  state.activeJourneyPath = activeJourneyPath;

  // Page Highlights: versioned separately so welcome version bumps do not wipe tours
  let completedPageTours = readJsonArray(STORAGE_KEYS.completedPageTours);
  let dismissedPageTourInvitations = readJsonArray(
    STORAGE_KEYS.dismissedPageTourInvitations
  );
  const storedPhVersion = localStorage.getItem(STORAGE_KEYS.pageHighlightsVersion);
  if (storedPhVersion == null || Number(storedPhVersion) !== PAGE_HIGHLIGHTS_VERSION) {
    completedPageTours = [];
    dismissedPageTourInvitations = [];
    try {
      localStorage.setItem(
        STORAGE_KEYS.pageHighlightsVersion,
        String(PAGE_HIGHLIGHTS_VERSION)
      );
      writeJsonArray(STORAGE_KEYS.completedPageTours, []);
      writeJsonArray(STORAGE_KEYS.dismissedPageTourInvitations, []);
    } catch {
      /* ignore */
    }
  }
  state.completedPageTours = completedPageTours;
  state.dismissedPageTourInvitations = dismissedPageTourInvitations;

  state.hydrated = true;

  expireStaleJourney();
  persistJourney();
}

hydrateFromStorage();

function persistWelcomeFlags() {
  try {
    localStorage.setItem(STORAGE_KEYS.version, String(TUTORIAL_VERSION));
    localStorage.setItem(
      STORAGE_KEYS.welcomeCompleted,
      state.welcomeCompleted ? 'true' : 'false'
    );
    if (state.tutorialSkipped) {
      localStorage.setItem(STORAGE_KEYS.skipped, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEYS.skipped);
    }
    if (state.interest) {
      localStorage.setItem(STORAGE_KEYS.interest, state.interest);
    }
  } catch {
    /* ignore */
  }
}

function persistCompleted() {
  writeJsonArray(STORAGE_KEYS.completed, [...state.completedItems]);
}

function persistDismissed() {
  writeJsonArray(STORAGE_KEYS.dismissed, [...state.dismissedCoachmarks]);
}

function claimInterrupt(kind) {
  if (state.activeInterrupt && state.activeInterrupt !== kind) {
    if (kind === 'coachmark' && state.activeInterrupt === 'welcome') return false;
    if (kind === 'welcome') {
      if (state.activeInterrupt === 'coachmark') {
        state.activeCoachmarkId = null;
        state.activeInterrupt = 'welcome';
        return true;
      }
      return false;
    }
    return false;
  }
  state.activeInterrupt = kind;
  return true;
}

function releaseInterrupt(kind) {
  if (state.activeInterrupt === kind) {
    state.activeInterrupt = null;
  }
}

function shouldSuppressNotLoggedIn() {
  try {
    if (sessionStorage.getItem(STORAGE_KEYS.welcomeSession) === 'true') return true;
  } catch {
    /* ignore */
  }
  return state.welcomeOpen || state.activeInterrupt === 'welcome';
}

function shouldShowWelcome() {
  const storedVersion = localStorage.getItem(STORAGE_KEYS.version);
  const versionChanged =
    storedVersion == null || Number(storedVersion) !== TUTORIAL_VERSION;
  return versionChanged || (!state.welcomeCompleted && !state.tutorialSkipped);
}

function evaluateWelcome({ homeReady = false } = {}) {
  if (homeReady) state.homeReady = true;
  if (!state.hydrated) hydrateFromStorage();
  if (!state.homeReady) return false;
  if (state.welcomeOpen || state.checklistOpen) return false;
  if (!shouldShowWelcome()) {
    // restore arrival after refresh if journey active
    maybeShowPendingArrival();
    return false;
  }
  if (!claimInterrupt('welcome')) return false;

  try {
    sessionStorage.setItem(STORAGE_KEYS.welcomeSession, 'true');
  } catch {
    /* ignore */
  }

  state.welcomeScreen = 1;
  state.welcomeOpen = true;
  logTutorialEvent('tutorial_welcome_viewed');
  return true;
}

function closeWelcome({ skipped = false, completed = false } = {}) {
  state.welcomeOpen = false;
  state.welcomeCompleted = true;
  if (skipped) state.tutorialSkipped = true;
  if (completed) state.tutorialSkipped = false;
  persistWelcomeFlags();
  releaseInterrupt('welcome');
  flushPendingCoachmark();
}

function skipWelcome() {
  closeWelcome({ skipped: true });
  logTutorialEvent('tutorial_welcome_skipped');
}

function completeWelcome() {
  closeWelcome({ completed: true });
  state.tutorialStarted = true;
  persistJourney();
  logTutorialEvent('tutorial_welcome_completed');
  if (state.interest) {
    logTutorialEvent(`tutorial_interest_${state.interest}`);
  }
  // Do NOT auto-complete Home. Open checklist so user can start journeys.
  openChecklist();
}

function setInterest(interestId) {
  if (!Object.values(INTERESTS).includes(interestId)) return;
  state.interest = interestId;
  try {
    localStorage.setItem(STORAGE_KEYS.interest, interestId);
  } catch {
    /* ignore */
  }
  logTutorialEvent(`tutorial_interest_selected_${interestId}`);
}

function openChecklist() {
  if (state.activeCoachmarkId) {
    dismissCoachmark({ silent: true, persistDismiss: false });
  }
  exitPageTour({ silent: true });
  dismissPageTourInvitation({ persist: false });
  state.tutorialStarted = true;
  persistJourney();
  state.checklistOpen = true;
  logTutorialEvent('tutorial_checklist_opened');
}

function closeChecklist() {
  state.checklistOpen = false;
  flushPendingCoachmark();
  maybeShowPendingArrival();
  setTimeout(() => {
    const tourId = resolvePageTourIdForPath(router.currentRoute.value?.path || '');
    if (tourId) offerPageTour(tourId);
  }, 400);
}

function isItemComplete(id) {
  return state.completedItems.includes(id);
}

function primaryItemsIncomplete() {
  return PRIMARY_ITEMS.some((item) => !isItemComplete(item.id));
}

function shouldShowLauncher() {
  if (!state.tutorialStarted) return false;
  if (state.launcherDismissedSession) return false;
  if (state.welcomeOpen || state.checklistOpen) return false;
  if (state.journeyLoading) return false;
  if (state.activeCoachmarkId) return false;
  if (state.activePageTourId || state.pageTourInvitationVisible) return false;
  if (!primaryItemsIncomplete() && !state.activeTutorialItemId) return false;
  return true;
}

function dismissLauncherSession() {
  state.launcherDismissedSession = true;
  try {
    sessionStorage.setItem(STORAGE_KEYS.launcherDismissedSession, 'true');
  } catch {
    /* ignore */
  }
}

function markItemComplete(id) {
  if (!id || isItemComplete(id)) return false;
  const valid = getAllItemIds().includes(id);
  if (!valid) return false;
  state.completedItems.push(id);
  persistCompleted();
  logTutorialEvent(`tutorial_item_completed_${id}`);
  if (state.activeTutorialItemId === id) {
    logTutorialEvent(`tutorial_journey_completed_${id}`);
    clearActiveJourney();
  }
  return true;
}

function isCoachmarkDismissed(id) {
  return state.dismissedCoachmarks.includes(id);
}

function requestCoachmark(id) {
  if (!id || !COACHMARKS[id]) return false;
  // Feature tips respect dismiss. Arrival tips during an active journey always allow show once per journey.
  const isArrival = String(id).startsWith('arrival-');
  if (!isArrival && isCoachmarkDismissed(id)) return false;

  // Arrival / feature coachmarks outrank Page Highlights
  if (state.pageTourInvitationVisible) dismissPageTourInvitation({ persist: false });
  if (state.activePageTourId) exitPageTour({ silent: true });

  if (state.welcomeOpen || state.checklistOpen) {
    state.pendingCoachmarkId = id;
    return false;
  }
  if (state.activeCoachmarkId === id) return true;
  if (state.activeCoachmarkId) {
    // Prefer replacing arrival with feature tip; otherwise queue
    const activeIsArrival = String(state.activeCoachmarkId).startsWith('arrival-');
    if (activeIsArrival && !isArrival) {
      state.activeCoachmarkId = null;
      releaseInterrupt('coachmark');
    } else {
      state.pendingCoachmarkId = id;
      return false;
    }
  }
  if (!claimInterrupt('coachmark')) {
    state.pendingCoachmarkId = id;
    return false;
  }
  state.activeCoachmarkId = id;
  if (isArrival && state.activeTutorialItemId) {
    state.arrivalShownForItem = state.activeTutorialItemId;
    logTutorialEvent(`tutorial_arrival_shown_${state.activeTutorialItemId}`);
  } else {
    logTutorialEvent(`tutorial_coachmark_viewed_${id}`);
  }
  return true;
}

function dismissCoachmark({ silent = false, id = null, persistDismiss = true } = {}) {
  const coachmarkId = id || state.activeCoachmarkId;
  if (!coachmarkId) return;

  const isArrival = String(coachmarkId).startsWith('arrival-');
  const itemId = state.activeTutorialItemId;

  if (persistDismiss && !isArrival && !isCoachmarkDismissed(coachmarkId)) {
    state.dismissedCoachmarks.push(coachmarkId);
    persistDismissed();
  }

  if (state.activeCoachmarkId === coachmarkId) {
    state.activeCoachmarkId = null;
    releaseInterrupt('coachmark');
  }

  if (isArrival && itemId && state.pendingArrivalId === coachmarkId) {
    state.pendingArrivalId = null;
    persistJourney();
    // Passive items complete after arrival is presented (shown or dismissed)
    const item = getPrimaryItem(itemId);
    if (item?.completion === 'passive' && state.arrivalShownForItem === itemId) {
      const path = router.currentRoute.value?.path || '';
      if (pathMatchesJourney(path, item)) {
        markItemComplete(itemId);
      }
    }
  }

  if (!silent) {
    logTutorialEvent(`tutorial_coachmark_dismissed_${coachmarkId}`);
  }
  flushPendingCoachmark();
  // If an arrival/feature tip was blocking a page invite, retry once
  setTimeout(() => {
    const tourId = resolvePageTourIdForPath(router.currentRoute.value?.path || '');
    if (tourId) offerPageTour(tourId);
  }, 350);
}

function flushPendingCoachmark() {
  if (!state.pendingCoachmarkId || state.welcomeOpen || state.checklistOpen || state.activeCoachmarkId) {
    return;
  }
  const next = state.pendingCoachmarkId;
  state.pendingCoachmarkId = null;
  requestCoachmark(next);
}

function replayCoachmark(id) {
  if (!id || !COACHMARKS[id]) return;
  state.dismissedCoachmarks = state.dismissedCoachmarks.filter((x) => x !== id);
  persistDismissed();
  state.pendingCoachmarkId = id;
  if (!state.welcomeOpen && !state.checklistOpen && !state.activeCoachmarkId) {
    requestCoachmark(id);
  }
}

function maybeShowPendingArrival() {
  if (!state.pendingArrivalId || !state.activeTutorialItemId) return;
  if (state.welcomeOpen || state.checklistOpen) return;
  const item = getPrimaryItem(state.activeTutorialItemId);
  if (!item) return;
  const path = router.currentRoute.value?.path || '';
  const onExpected = pathMatchesJourney(path, item);

  if (!onExpected) {
    // Hide stale arrival without clearing journey / pending resume
    if (state.activeCoachmarkId === state.pendingArrivalId) {
      state.activeCoachmarkId = null;
      releaseInterrupt('coachmark');
    }
    return;
  }

  if (state.pendingArrivalId && !state.activeCoachmarkId) {
    requestCoachmark(state.pendingArrivalId);
  }
}

function startJourney(item) {
  if (!item) return;
  const old = state.activeTutorialItemId;
  if (old && old !== item.id) {
    logTutorialEvent(`tutorial_journey_replaced_${old}_${item.id}`);
    // clear stale arrival/coachmark from previous item
    if (state.activeCoachmarkId) {
      dismissCoachmark({ silent: true, persistDismiss: false });
    }
    state.pendingCoachmarkId = null;
    state.pendingArrivalId = null;
    state.arrivalShownForItem = null;
  }

  state.tutorialStarted = true;
  state.activeTutorialItemId = item.id;
  state.activeJourneyStartedAt = Date.now();
  state.pendingArrivalId = item.arrivalId || null;
  state.arrivalShownForItem = null;
  state.activeJourneyPath = null;
  persistJourney();
  logTutorialEvent(`tutorial_journey_started_${item.id}`);
  logTutorialEvent(`tutorial_item_opened_${item.id}`);
}

function buildArticlePath(article) {
  if (!article) return null;
  if (typeof article.urlPath === 'string' && article.urlPath.startsWith('/')) {
    return article.urlPath;
  }
  const id = article.id ?? article.articleId;
  if (id == null) return null;
  const category = article.categorySpecified || article.category || 'politics';
  const title = article.title || 'article';
  const slug = String(title)
    .toLowerCase()
    .replace(/\W+/g, '-')
    .replace(/^-|-$/g, '');
  return `/${category}/${slug}/${id}`;
}

function isLikelyArticlePath(path) {
  if (!path) return false;
  const parts = String(path).split('/').filter(Boolean);
  return parts.length >= 3 && /^\d+$/.test(parts[parts.length - 1]);
}

function pathMatchesJourney(path, item) {
  if (!path || !item) return false;
  if (pathMatchesExpected(path, item.expectedPaths || [])) return true;
  if (state.activeJourneyPath) {
    const target = state.activeJourneyPath.split('?')[0];
    const current = path.split('?')[0];
    if (current === target) return true;
  }
  // Bias / Take live on article pages after resolve
  if (item.destination?.type === 'politics-article' && isLikelyArticlePath(path)) {
    return true;
  }
  return false;
}

async function resolvePoliticsArticlePath() {
  const fetchers = [
    () => ArticleService.listCenterArticles(),
    () => ArticleService.listLeftWingArticles(),
    () => ArticleService.listRightWingArticles(),
    () => ArticleService.listPoliticsArticles(),
  ];

  for (const fetch of fetchers) {
    try {
      const res = await fetch();
      const list = Array.isArray(res?.data) ? res.data : [];
      for (const article of list) {
        const path = buildArticlePath(article);
        if (path) return path;
      }
    } catch {
      /* try next source */
    }
  }
  return null;
}

function navigateToPath(path, { isPrimary = false, hash = null } = {}) {
  const cleanPath = String(path || '/').split('#')[0];
  const anchor = hash ? String(hash).replace(/^#/, '') : null;
  const routeTarget = anchor ? { path: cleanPath, hash: `#${anchor}` } : cleanPath;

  const current = router.currentRoute.value?.path || '';
  const alreadyThere =
    current === cleanPath ||
    (cleanPath !== '/' && current.startsWith(cleanPath) && cleanPath !== '/politics/') ||
    (cleanPath === '/' && (current === '/' || current === ''));

  const after = ({ alreadyMounted = false } = {}) => {
    if (isPrimary) setTimeout(() => maybeShowPendingArrival(), 50);
    if (anchor) {
      scrollToTutorialAnchor(anchor);
      // Overlay clears when the anchor is found (or retries exhaust)
      return;
    }
    const item = getPrimaryItem(state.activeTutorialItemId);
    if (item?.completion === 'passive') {
      if (alreadyMounted) {
        // Ready hooks may not re-fire on the same route
        setTimeout(() => {
          if (!state.journeyLoading) return;
          if (state.activeTutorialItemId) {
            onPassiveDestinationReady(state.activeTutorialItemId);
          }
          if (state.journeyLoading) endJourneyLoading();
        }, 250);
      }
      // Fresh navigation: wait for onPassiveDestinationReady from the page
      return;
    }
    // Feature journeys without an in-page anchor (e.g. Compare discovery)
    setTimeout(() => {
      if (state.journeyLoading) endJourneyLoading();
    }, 400);
  };

  if (alreadyThere) {
    if (anchor && router.currentRoute.value?.hash !== `#${anchor}`) {
      router.replace({ hash: `#${anchor}` }).then(() => after()).catch(() => after());
      return;
    }
    after({ alreadyMounted: true });
    return;
  }
  router.push(routeTarget).then(() => after()).catch(() => {
    after();
    endJourneyLoading();
  });
}

/**
 * Scroll to a tutorial feature anchor. Retries while article widgets mount.
 */
function scrollToTutorialAnchor(anchor, { attempts = 0 } = {}) {
  if (!anchor || typeof document === 'undefined') return false;
  const el = document.getElementById(anchor);
  if (el) {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start',
    });
    endJourneyLoading();
    return true;
  }
  if (attempts >= 24) {
    endJourneyLoading();
    return false;
  }
  setTimeout(() => scrollToTutorialAnchor(anchor, { attempts: attempts + 1 }), 120);
  return false;
}

/**
 * Widgets call this when they become visible so late-loading content still scrolls into view.
 */
function maybeScrollActiveFeatureIntoView(anchor) {
  if (!anchor) return;
  const hash = (router.currentRoute.value?.hash || '').replace(/^#/, '');
  const item = getPrimaryItem(state.activeTutorialItemId);
  const journeyAnchor = item?.destination?.anchor;
  if (hash !== anchor && journeyAnchor !== anchor) return;
  scrollToTutorialAnchor(anchor);
}

function openItem(item) {
  if (!item) return;

  const isPrimary = !!getPrimaryItem(item.id);
  if (isPrimary) {
    startJourney(item);
  } else {
    state.tutorialStarted = true;
    persistJourney();
    logTutorialEvent(`tutorial_item_opened_${item.id}`);
  }

  const dest = item.destination;
  if (dest && dest.type !== 'external') {
    beginJourneyLoading();
  }

  closeChecklist();

  if (!dest) {
    endJourneyLoading();
    if (isPrimary) maybeShowPendingArrival();
    return;
  }

  if (dest.type === 'external' && dest.url) {
    if (item.id === 'shop') markItemComplete('shop');
    window.open(dest.url, '_blank', 'noopener,noreferrer');
    endJourneyLoading();
    return;
  }

  if (dest.type === 'politics-article') {
    const fallback = dest.fallbackPath || '/politics/left-wing';
    const anchor = dest.anchor || null;
    resolvePoliticsArticlePath()
      .then((articlePath) => {
        const path = articlePath || fallback;
        state.activeJourneyPath = path;
        persistJourney();
        navigateToPath(path, {
          isPrimary,
          hash: articlePath ? anchor : null,
        });
      })
      .catch(() => {
        state.activeJourneyPath = fallback;
        persistJourney();
        navigateToPath(fallback, { isPrimary });
      });
    return;
  }

  if (dest.type === 'route' && dest.path) {
    if (item.requiresAuth) {
      try {
        sessionStorage.setItem(STORAGE_KEYS.resumePath, dest.path);
      } catch {
        /* ignore */
      }
    }

    // Optional stops: count as done once the user opens them
    if (!isPrimary && ['conversation', 'friends-following'].includes(item.id)) {
      markItemComplete(item.id);
    }

    state.activeJourneyPath = dest.path;
    persistJourney();
    navigateToPath(dest.path, { isPrimary, hash: dest.anchor || null });
  }
}

function consumeResumePath() {
  try {
    const path = sessionStorage.getItem(STORAGE_KEYS.resumePath);
    if (path) {
      sessionStorage.removeItem(STORAGE_KEYS.resumePath);
      return path;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function onRouteEntered(path) {
  if (!path) return;
  expireStaleJourney();

  // Don't cover the login form while auth-gated journeys resume
  if (state.journeyLoading && (path === '/login' || path.startsWith('/login'))) {
    endJourneyLoading();
  }

  // Leave page tours when the route changes
  if (state.activePageTourId || state.pageTourInvitationVisible) {
    const currentTourId =
      state.activePageTourId || state.pageTourInvitationId;
    const expected = currentTourId ? resolvePageTourIdForPath(path) : null;
    if (currentTourId && expected !== currentTourId) {
      exitPageTour({ silent: true });
      dismissPageTourInvitation({ persist: false });
    }
  }

  if (!state.activeTutorialItemId || !state.pendingArrivalId) return;

  const item = getPrimaryItem(state.activeTutorialItemId);
  if (!item) return;

  // Always re-evaluate arrival visibility against the current path
  setTimeout(() => maybeShowPendingArrival(), 80);
}

function isTargetVisible(targetId) {
  if (!targetId || typeof document === 'undefined') return false;
  const el = document.getElementById(targetId);
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  return true;
}

function clearPageTourHighlight() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(`.${PAGE_TOUR_HIGHLIGHT_CLASS}`).forEach((el) => {
    el.classList.remove(PAGE_TOUR_HIGHLIGHT_CLASS);
  });
}

function applyPageTourHighlight(targetId) {
  clearPageTourHighlight();
  if (!targetId || typeof document === 'undefined') return;
  const el = document.getElementById(targetId);
  if (!el) return;
  el.classList.add(PAGE_TOUR_HIGHLIGHT_CLASS);
  const reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  try {
    el.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'center',
    });
  } catch {
    /* ignore */
  }
}

function buildVisiblePageTourSteps(tourId) {
  const tour = getPageTour(tourId);
  if (!tour) return [];
  const steps = [];
  for (const step of tour.steps || []) {
    if (steps.length >= 4) break;
    if (step.setTab) {
      // Tab panels may be v-show hidden until the step activates them
      if (typeof document === 'undefined' || !document.getElementById(step.targetId)) {
        continue;
      }
      steps.push(step);
      continue;
    }
    if (!isTargetVisible(step.targetId)) continue;
    steps.push(step);
  }
  return steps;
}

function canOfferPageTour(tourId) {
  if (!tourId || !getPageTour(tourId)) return false;
  if (!state.welcomeCompleted && !state.tutorialStarted) return false;
  if (state.welcomeOpen || state.checklistOpen || state.journeyLoading) return false;
  if (state.activeCoachmarkId || state.pendingArrivalId) return false;
  if (state.activePageTourId || state.pageTourInvitationVisible) return false;
  if (state.completedPageTours.includes(tourId)) return false;
  if (state.dismissedPageTourInvitations.includes(tourId)) return false;
  return true;
}

function offerPageTour(tourId, { force = false } = {}) {
  if (!force && !canOfferPageTour(tourId)) return false;
  if (force) {
    if (!getPageTour(tourId)) return false;
    if (state.welcomeOpen || state.checklistOpen) return false;
  }
  state.pageTourInvitationId = tourId;
  state.pageTourInvitationVisible = true;
  logTutorialEvent(`tutorial_page_tour_invite_${tourId}`);
  return true;
}

function dismissPageTourInvitation({ persist = true } = {}) {
  const id = state.pageTourInvitationId;
  state.pageTourInvitationVisible = false;
  state.pageTourInvitationId = null;
  if (persist && id && !state.dismissedPageTourInvitations.includes(id)) {
    state.dismissedPageTourInvitations.push(id);
    writeJsonArray(
      STORAGE_KEYS.dismissedPageTourInvitations,
      [...state.dismissedPageTourInvitations]
    );
    logTutorialEvent(`tutorial_page_tour_invite_dismissed_${id}`);
  }
}

function startPageTour(tourId) {
  const tour = getPageTour(tourId);
  if (!tour) return false;
  if (state.welcomeOpen || state.checklistOpen) return false;

  dismissPageTourInvitation({ persist: false });
  if (state.activeCoachmarkId) {
    dismissCoachmark({ silent: true, persistDismiss: false });
  }

  const steps = buildVisiblePageTourSteps(tourId);
  if (!steps.length) {
    // Nothing visible yet; mark invitation dismissed so we do not loop forever
    if (!state.dismissedPageTourInvitations.includes(tourId)) {
      state.dismissedPageTourInvitations.push(tourId);
      writeJsonArray(
        STORAGE_KEYS.dismissedPageTourInvitations,
        [...state.dismissedPageTourInvitations]
      );
    }
    return false;
  }

  state.activePageTourId = tourId;
  state.activePageTourSteps = steps;
  state.activePageTourStep = 0;
  applyPageTourStep(0);
  logTutorialEvent(`tutorial_page_tour_started_${tourId}`);
  return true;
}

function applyPageTourStep(index) {
  const steps = state.activePageTourSteps || [];
  const step = steps[index];
  if (!step) return;
  state.activePageTourStep = index;
  if (step.setTab) {
    state.pageTourTabRequest = step.setTab;
  }
  // Allow tab swap / layout settle before measuring visibility again
  setTimeout(() => {
    if (state.activePageTourId && state.activePageTourStep === index) {
      applyPageTourHighlight(step.targetId);
    }
  }, step.setTab ? 80 : 0);
}

function pageTourNext() {
  if (!state.activePageTourId) return;
  const next = state.activePageTourStep + 1;
  if (next >= state.activePageTourSteps.length) {
    completePageTour();
    return;
  }
  applyPageTourStep(next);
}

function pageTourBack() {
  if (!state.activePageTourId) return;
  const prev = state.activePageTourStep - 1;
  if (prev < 0) return;
  applyPageTourStep(prev);
}

function completePageTour() {
  const id = state.activePageTourId;
  clearPageTourHighlight();
  state.activePageTourId = null;
  state.activePageTourStep = 0;
  state.activePageTourSteps = [];
  state.pageTourTabRequest = null;
  if (id && !state.completedPageTours.includes(id)) {
    state.completedPageTours.push(id);
    writeJsonArray(STORAGE_KEYS.completedPageTours, [...state.completedPageTours]);
  }
  if (id) logTutorialEvent(`tutorial_page_tour_completed_${id}`);
}

function exitPageTour({ silent = false } = {}) {
  const id = state.activePageTourId;
  clearPageTourHighlight();
  state.activePageTourId = null;
  state.activePageTourStep = 0;
  state.activePageTourSteps = [];
  state.pageTourTabRequest = null;
  if (!silent && id) logTutorialEvent(`tutorial_page_tour_exited_${id}`);
}

function acceptPageTourInvitation() {
  const id = state.pageTourInvitationId;
  if (!id) return false;
  state.pageTourInvitationVisible = false;
  state.pageTourInvitationId = null;
  return startPageTour(id);
}

function replayPageTour(tourId) {
  if (!getPageTour(tourId)) return false;
  state.completedPageTours = state.completedPageTours.filter((x) => x !== tourId);
  state.dismissedPageTourInvitations = state.dismissedPageTourInvitations.filter(
    (x) => x !== tourId
  );
  writeJsonArray(STORAGE_KEYS.completedPageTours, [...state.completedPageTours]);
  writeJsonArray(
    STORAGE_KEYS.dismissedPageTourInvitations,
    [...state.dismissedPageTourInvitations]
  );
  return startPageTour(tourId);
}

function getActivePageTourStep() {
  if (!state.activePageTourId) return null;
  const steps = state.activePageTourSteps || [];
  return steps[state.activePageTourStep] || null;
}

function getPageTourInvitation() {
  if (!state.pageTourInvitationVisible || !state.pageTourInvitationId) return null;
  return getPageTour(state.pageTourInvitationId);
}

/**
 * Meaningful feature events (Bias ready, Take vote, Compare open).
 * Completes when tutorial has been started; preferred when journey matches.
 */
function onFeatureEvent(itemId, { showFeatureTip = true } = {}) {
  if (!state.tutorialStarted && !state.activeTutorialItemId) return false;
  const item = getPrimaryItem(itemId);
  if (!item || item.completion !== 'feature') return false;

  const arrivalId = item.arrivalId;
  if (
    state.activeCoachmarkId === arrivalId ||
    (state.activeTutorialItemId === itemId &&
      state.activeCoachmarkId &&
      String(state.activeCoachmarkId).startsWith('arrival-'))
  ) {
    dismissCoachmark({ silent: true, persistDismiss: false });
  }
  if (
    state.pendingArrivalId &&
    (state.activeTutorialItemId === itemId || state.pendingArrivalId === arrivalId)
  ) {
    state.pendingArrivalId = null;
    persistJourney();
  }

  const completed = markItemComplete(itemId);

  endJourneyLoading();

  if (showFeatureTip && item.featureTipId) {
    requestCoachmark(item.featureTipId);
  }
  return completed;
}

/**
 * Passive destination rendered while journey is active.
 * Arrival must be shown (or requested) before completion on dismiss.
 */
function onPassiveDestinationReady(itemId) {
  if (state.activeTutorialItemId !== itemId) {
    return false;
  }
  const item = getPrimaryItem(itemId);
  if (!item || item.completion !== 'passive') return false;

  endJourneyLoading();

  if (state.pendingArrivalId) {
    requestCoachmark(state.pendingArrivalId);
  } else if (state.arrivalShownForItem === itemId) {
    markItemComplete(itemId);
    return true;
  } else if (item.arrivalId) {
    requestCoachmark(item.arrivalId);
  } else {
    markItemComplete(itemId);
  }
  return false;
}

function onTakeCardVisible() {
  if (!state.tutorialStarted) return;

  if (state.activeTutorialItemId === 'barbershop-take') {
    if (
      state.activeCoachmarkId &&
      String(state.activeCoachmarkId).startsWith('arrival-')
    ) {
      dismissCoachmark({ silent: true, persistDismiss: false });
    }
    if (state.pendingArrivalId === 'arrival-barbershop-take') {
      state.pendingArrivalId = null;
      persistJourney();
    }
    requestCoachmark('barbershop-take');
    return;
  }

  // Natural discovery after tutorial start: do not steal another journey's arrival
  if (!state.activeTutorialItemId && !state.activeCoachmarkId) {
    requestCoachmark('barbershop-take');
  }
}

function orderedPrimaryItems() {
  const order = INTEREST_ORDER[state.interest] || PRIMARY_ITEMS.map((i) => i.id);
  return order.map((id) => getPrimaryItem(id)).filter(Boolean);
}

function primaryProgress() {
  const total = PRIMARY_ITEMS.length || 8;
  const done = PRIMARY_ITEMS.filter((i) => isItemComplete(i.id)).length;
  return { done, total, percent: Math.round((done / total) * 100) };
}

function replayFromHelp() {
  state.launcherDismissedSession = false;
  try {
    sessionStorage.removeItem(STORAGE_KEYS.launcherDismissedSession);
  } catch {
    /* ignore */
  }
  logTutorialEvent('tutorial_replayed');
  openChecklist();
}

function continueExploringClicked() {
  logTutorialEvent('tutorial_continue_clicked');
  openChecklist();
}

function getActiveCoachmark() {
  if (!state.activeCoachmarkId) return null;
  return getCoachmark(state.activeCoachmarkId);
}

export function useTutorialProgress() {
  return {
    state,
    TUTORIAL_VERSION,
    PRIMARY_ITEMS,
    OPTIONAL_ITEMS,
    evaluateWelcome,
    shouldShowWelcome,
    shouldSuppressNotLoggedIn,
    skipWelcome,
    completeWelcome,
    setInterest,
    openChecklist,
    closeChecklist,
    isItemComplete,
    markItemComplete,
    requestCoachmark,
    dismissCoachmark,
    replayCoachmark,
    isCoachmarkDismissed,
    orderedPrimaryItems,
    primaryProgress,
    openItem,
    onRouteEntered,
    onFeatureEvent,
    onPassiveDestinationReady,
    onTakeCardVisible,
    maybeShowPendingArrival,
    maybeScrollActiveFeatureIntoView,
    shouldShowLauncher,
    dismissLauncherSession,
    continueExploringClicked,
    replayFromHelp,
    getActiveCoachmark,
    clearActiveJourney,
    claimInterrupt,
    releaseInterrupt,
    getPrimaryItem,
    getOptionalItem,
    consumeResumePath,
    offerPageTour,
    acceptPageTourInvitation,
    dismissPageTourInvitation,
    startPageTour,
    pageTourNext,
    pageTourBack,
    exitPageTour,
    completePageTour,
    replayPageTour,
    getActivePageTourStep,
    getPageTourInvitation,
    getPageTour,
  };
}

export {
  state as tutorialState,
  evaluateWelcome,
  shouldSuppressNotLoggedIn,
  openChecklist,
  markItemComplete,
  requestCoachmark,
  dismissCoachmark,
  onRouteEntered,
  onFeatureEvent,
  onPassiveDestinationReady,
  onTakeCardVisible,
  replayFromHelp,
  shouldShowLauncher,
  consumeResumePath,
  maybeScrollActiveFeatureIntoView,
  offerPageTour,
  exitPageTour,
};
